import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles, ExternalLink } from "lucide-react";
import api from "../lib/api";

const mkId = () => (crypto.randomUUID && crypto.randomUUID()) || `m_${Date.now()}_${Math.random()}`;

const WELCOME = {
  id: "welcome",
  role: "assistant",
  content: "Hi! I'm Ask E.O.N. Ask me anything about Boerboels — breed traits, health, nutrition, training or guardian ownership.",
  citations: [],
};

export function AskEon() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const sessionRef = useRef((crypto.randomUUID && crypto.randomUUID()) || String(Date.now()));

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy, open]);

  const send = async (e) => {
    e?.preventDefault();
    const q = input.trim();
    if (!q || busy) return;
    const history = messages.filter((m) => m !== WELCOME).map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { id: mkId(), role: "user", content: q, citations: [] }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ask", { question: q, session_id: sessionRef.current, history });
      setMessages((prev) => [...prev, { id: mkId(), role: "assistant", content: data.answer, citations: data.citations || [] }]);
    } catch {
      setMessages((prev) => [...prev, { id: mkId(), role: "assistant", content: "Sorry, something went wrong. Please try again.", citations: [] }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="ask-eon-open"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-eon-gold text-white font-bold text-sm px-5 py-3.5 shadow-xl hover:bg-eon-goldhover transition-colors">
          <Sparkles size={18} /> Ask E.O.N
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[calc(100vw-2.5rem)] sm:w-[380px] h-[540px] max-h-[80vh] flex flex-col rounded-2xl border border-eon-border bg-white shadow-2xl overflow-hidden" data-testid="ask-eon-panel">
          <div className="flex items-center justify-between px-4 py-3 bg-eon-text text-white">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-eon-gold flex items-center justify-center"><Sparkles size={16} className="text-white" /></span>
              <div><div className="font-display font-bold text-sm leading-tight">Ask E.O.N</div><div className="text-[10px] text-white/60">Boerboel AI · answers with sources</div></div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ask-eon-close" className="text-white/70 hover:text-white p-1"><X size={18} /></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-eon-surface/40" data-testid="ask-eon-messages">
            {messages.map((m, i) => (
              <div key={m.id || i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-eon-gold text-white rounded-br-sm" : "bg-white border border-eon-border text-eon-text rounded-bl-sm"}`}>
                  {m.content}
                  {m.citations?.length > 0 && (
                    <div className="mt-2.5 pt-2.5 border-t border-eon-border/70 space-y-1">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-eon-muted">Sources</div>
                      {m.citations.slice(0, 5).map((url) => (
                        <a key={url} href={url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[11px] text-eon-gold hover:underline truncate">
                          <ExternalLink size={10} className="shrink-0" /> <span className="truncate">{url.replace(/^https?:\/\//, "")}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start"><div className="bg-white border border-eon-border rounded-2xl rounded-bl-sm px-4 py-3">
                <span className="flex gap-1">
                  <span className="w-2 h-2 rounded-full bg-eon-gold/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 rounded-full bg-eon-gold/60 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 rounded-full bg-eon-gold/60 animate-bounce" style={{ animationDelay: "300ms" }} />
                </span>
              </div></div>
            )}
          </div>

          <form onSubmit={send} className="p-3 border-t border-eon-border bg-white flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about Boerboels..." data-testid="ask-eon-input"
              className="flex-1 bg-eon-surface border border-eon-border rounded-full px-4 py-2.5 text-sm outline-none focus:border-eon-gold" />
            <button type="submit" disabled={busy || !input.trim()} data-testid="ask-eon-send"
              className="w-10 h-10 rounded-full bg-eon-gold text-white flex items-center justify-center hover:bg-eon-goldhover transition-colors disabled:opacity-40 shrink-0"><Send size={16} /></button>
          </form>
        </div>
      )}
    </>
  );
}
